import { clearClientCreateResult } from "@/components/onboarding/client-create-result";
import { Permissions } from "@/constants/permissions";
import { useSupportSummary } from "@/hooks/use-support";
import { cn } from "@/lib/utils";
import { useAdminStore } from "@/stores/admin-store";
import { useAdminUserContextStore } from "@/stores/admin-user-context-store";
import { useMsal } from "@azure/msal-react";
import { Link, useLocation } from "@tanstack/react-router";
import {
	Activity,
	BookOpen,
	Box,
	Building2,
	ChevronLeft,
	ClipboardList,
	CreditCard,
	FileCheck,
	Gauge,
	Handshake,
	Layers,
	LayoutDashboard,
	LifeBuoy,
	ListChecks,
	LogOut,
	type LucideIcon,
	MapPin,
	Receipt,
	Shield,
	User,
	Users,
} from "lucide-react";
import type { ReactNode } from "react";
import { CLIENT_ACCENT, useClientContextNavigation } from "./client-context";

/**
 * Two menus: Global Administration (slate) and client mode (teal accent —
 * `CLIENT_ACCENT`). Client mode also gets a coloured header band and edge so
 * the mode is obvious at a glance; the page banner (`ClientContextBanner`)
 * repeats it on the content side.
 */
export function Sidebar() {
	const selectedClientId = useAdminStore((s) => s.selectedClientId);
	const selectedClientName = useAdminStore((s) => s.selectedClientName);
	const { exitClient } = useClientContextNavigation();
	const location = useLocation();
	// Reactive read so the links appear once the admin context has loaded.
	const permissions = useAdminUserContextStore((s) => s.context.permissions);
	const canManageChecklistLibrary = permissions.includes(
		Permissions.ChecklistLibrary.Manage,
	);
	const clientMode = !!selectedClientId;
	const canViewSupport = permissions.includes(
		Permissions.Support.ViewTickets,
	);
	const canViewClientHealth = permissions.includes(
		Permissions.Dashboard.ViewClientHealth,
	);
	// Tickets waiting on a staff reply: the Support badge.
	const { data: supportSummary } = useSupportSummary(canViewSupport);
	const needsReply = supportSummary?.needsReply ?? 0;
	const { instance, accounts, inProgress } = useMsal();
	const clearClientContext = useAdminStore((s) => s.clearClientContext);

	// Drop what this browser remembers for the admin (the client being worked
	// on is persisted in localStorage) before handing off to Microsoft, which
	// ends the session and returns to the sign-in page.
	const signOut = () => {
		if (inProgress !== "none") return;
		clearClientContext();
		clearClientCreateResult();
		instance
			.logoutRedirect({
				account: instance.getActiveAccount() ?? accounts[0],
			})
			.catch((e) => console.error(e));
	};

	const isActive = (path: string, exact = false) =>
		exact
			? location.pathname === path
			: location.pathname === path ||
				location.pathname.startsWith(`${path}/`);

	const item = (
		to: string,
		label: string,
		Icon: LucideIcon,
		options: { exact?: boolean; badge?: number; badgeTitle?: string } = {},
	) => (
		<li>
			<Link
				to={to}
				className={cn(
					"flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
					clientMode
						? CLIENT_ACCENT.navHover
						: "hover:bg-slate-800 hover:text-white",
					isActive(to, options.exact) &&
						(clientMode
							? CLIENT_ACCENT.navActive
							: "bg-slate-800 text-white"),
				)}
			>
				<Icon className="w-4 h-4" />
				<span className="flex-1">{label}</span>
				{!!options.badge && (
					<span
						className="rounded-full bg-blue-600 px-2 py-0.5 text-xs font-semibold text-white"
						title={options.badgeTitle}
					>
						{options.badge}
					</span>
				)}
			</Link>
		</li>
	);

	const section = (label: ReactNode) => (
		<li
			className={cn(
				"pt-4 pb-2 px-3 text-xs font-semibold uppercase tracking-wider",
				clientMode ? CLIENT_ACCENT.sectionLabel : "text-slate-500",
			)}
		>
			{label}
		</li>
	);

	return (
		<aside
			className={cn(
				"w-64 bg-slate-900 text-slate-300 flex flex-col h-screen",
				clientMode
					? CLIENT_ACCENT.sidebarEdge
					: "border-r border-slate-800",
			)}
		>
			{/* Header */}
			<div className="h-14 flex items-center px-4 border-b border-slate-800">
				<span className="font-bold text-white text-lg tracking-tight">
					InspectTec <span className="text-blue-500">Admin</span>
				</span>
			</div>

			{/* Context indicator */}
			{clientMode ? (
				<div className={cn("px-4 py-3", CLIENT_ACCENT.band)}>
					<div
						className={cn(
							"text-xs font-semibold uppercase tracking-wide mb-1",
							CLIENT_ACCENT.bandMuted,
						)}
					>
						Managing client
					</div>
					<div
						className={cn(
							"text-base font-bold truncate",
							CLIENT_ACCENT.bandText,
						)}
						title={selectedClientName ?? undefined}
					>
						{selectedClientName}
					</div>
					<button
						type="button"
						onClick={exitClient}
						className={cn(
							"text-xs flex items-center gap-1 mt-2 transition-colors hover:text-white hover:underline",
							CLIENT_ACCENT.bandMuted,
						)}
					>
						<ChevronLeft className="w-3 h-3" />
						Back to Global Admin
					</button>
				</div>
			) : (
				<div className="px-4 py-3 border-b border-slate-800">
					<div className="text-xs font-medium text-slate-500 uppercase">
						Context
					</div>
					<div className="text-sm font-bold text-slate-200">
						Global Administration
					</div>
				</div>
			)}

			{/* Navigation */}
			<nav className="flex-1 overflow-y-auto py-4">
				<ul className="space-y-1 px-2">
					{!clientMode ? (
						<>
							{item("/", "Dashboard", LayoutDashboard, {
								exact: true,
							})}
							{canViewSupport &&
								item("/support", "Support", LifeBuoy, {
									badge: needsReply,
									badgeTitle: `${needsReply} waiting for a reply`,
								})}
							{canViewClientHealth &&
								item(
									"/client-health",
									"Client Health",
									Activity,
								)}
							{section("Platform")}
							{item("/clients", "Clients", Building2)}
							{item("/agencies", "Agencies", Building2)}
							{item("/users", "Global Admins", Users)}
							{item("/admin-roles", "Global Roles", Shield)}
							{section("Billing")}
							{item("/plans", "Plans", Layers)}
							{item("/associations", "Associations", Handshake)}
							{item(
								"/subscriptions",
								"Subscriptions",
								CreditCard,
							)}
							{item("/invoices", "Invoices", Receipt)}
							{item("/quota-report", "Quota Report", Gauge)}
							{section("Libraries")}
							{item(
								"/course-library",
								"Course Library",
								BookOpen,
							)}
							{canManageChecklistLibrary &&
								item(
									"/checklist-library",
									"Checklist Library",
									ClipboardList,
								)}
						</>
					) : (
						<>
							{item(
								"/client/dashboard",
								"Client Dashboard",
								LayoutDashboard,
							)}
							{item(
								"/client/onboarding",
								"Onboarding",
								FileCheck,
							)}
							{section("Client Management")}
							{item(
								"/client/details",
								"Client Details",
								Building2,
							)}
							{section("Asset Management")}
							{item("/client/assets", "Assets", Box)}
							{item("/client/asset-groups", "Asset Groups", Box)}
							{item("/client/locations", "Locations", MapPin)}
							{section("Inspection Config")}
							{item(
								"/client/checklists",
								"Checklists",
								ListChecks,
							)}
							{canManageChecklistLibrary &&
								item(
									"/client/checklist-library",
									"Checklist Library",
									ClipboardList,
								)}
							{item(
								"/client/layout-modes",
								"Layout Modes",
								Layers,
							)}
							{section("Data & Users")}
							{item("/client/roles", "Roles", Shield)}
							{item("/client/groups", "User Groups", Users)}
							{item("/client/users", "Users", User)}
						</>
					)}
				</ul>
			</nav>

			{/* Footer */}
			<div className="p-4 border-t border-slate-800">
				<button
					type="button"
					onClick={signOut}
					disabled={inProgress !== "none"}
					className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors w-full disabled:opacity-50 disabled:cursor-not-allowed"
				>
					<LogOut className="w-4 h-4" />
					Sign Out
				</button>
			</div>
		</aside>
	);
}
